import { clsx } from "clsx";
import pluralize from "pluralize";
import { type FC, useCallback, useEffect, useState } from "react";
import type { AriaMenuProps } from "react-aria";
import { useDispatch } from "react-redux";
import { Item, MenuListBox, MenuOptionItem } from "widgets";

import { getIconElementForPlaybook, loadPlaybooks } from "@/app-utils/playbook.ts";
import type { Group, VariantAnalysisGroup } from "@/graphql.ts";
import { type InputTableQuerySpec, type Playbook, UiContextEnum } from "@/graphql.ts";
import { getRelatedAnalysisGroups } from "@/redux/modules/genome-browser.ts";
import { updateNfPlaybookExecutionState } from "@/redux/modules/nf-playbook-execution.ts";
import { openPlaybook } from "@/redux/modules/playbook-execution.ts";
import { shouldLoadGroup } from "@/redux/modules/saved-group.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { DATATABLE_ID_TYPE, ENTITY_TYPE, FEATURE_DISPLAY_NAME, PLAYBOOK_TYPE } from "@/settings/settings.ts";
import { ProtectedResourceLogo } from "@/ui-modules/core/ProtectedResourceLogo.tsx";
import menuStyle from "@/ui-modules/core/styles/ContextualMenu.module.scss";

interface IReportPlaybooksMenuDialogProps<T> extends AriaMenuProps<T> {
  tableQuerySpec: InputTableQuerySpec;
}

export const ReportPlaybooksMenuDialog: FC<IReportPlaybooksMenuDialogProps<any>> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const tableQuerySpec = props.tableQuerySpec;

  const [isLoading, setLoading] = useState<boolean>(true);
  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [analysisGroupFeedback, setAnalysisGroupFeedback] = useState<string>();

  useEffect(() => {
    void Promise.all([loadReportPlaybooks(), checkAnalysisGroup()]).then(() => {
      setLoading(false);
    });
  }, []);

  const checkAnalysisGroup = useCallback(async () => {
    if (tableQuerySpec.queryType === ENTITY_TYPE.ORGANISM) {
      // VCF variants case, always part of an analysis group
    } else if (tableQuerySpec.queryType === ENTITY_TYPE.COORD_LIST) {
      void dispatch(getRelatedAnalysisGroups(tableQuerySpec.restrictions.refGenomeId)).then(
        (analysisGroups: VariantAnalysisGroup[]) => {
          if (!analysisGroups.length) {
            setAnalysisGroupFeedback(
              `You may not be able to successfully create a report. The reference genome for the selected coordinates does not belong to an analysis group.`,
            );
          }
        },
      );
    } else if (tableQuerySpec.idType === DATATABLE_ID_TYPE.GROUP) {
      void dispatch(shouldLoadGroup(tableQuerySpec.id)).then((group: Group) => {
        if (!group.analysisGroup) {
          setAnalysisGroupFeedback(
            `You may not be able to successfully create a report. The reference genome for the selected variants does not belong to an analysis group.`,
          );
        }
      });
    }
  }, []);

  const loadReportPlaybooks = useCallback(async () => {
    const playbooks = await dispatch(loadPlaybooks());

    let filteredPlaybooks: Playbook[] = [];
    if (tableQuerySpec.resultType === ENTITY_TYPE.VCF_VARIANT) {
      // "coordinates" reports should be available from both variants and coordinates contexts
      filteredPlaybooks = playbooks.filter(
        (p) =>
          p.type?.id === PLAYBOOK_TYPE.REPORT_FROM_VARIANTS ||
          p.type?.id === PLAYBOOK_TYPE.REPORT_FROM_COORDINATES ||
          (p.type?.id === PLAYBOOK_TYPE.PROCESSOR && p.uiContexts?.find((c) => c === UiContextEnum.Variants)),
      );
    } else if (tableQuerySpec.resultType === ENTITY_TYPE.COORDINATE) {
      filteredPlaybooks = playbooks.filter(
        (p) =>
          p.type?.id === PLAYBOOK_TYPE.REPORT_FROM_COORDINATES ||
          (p.type?.id === PLAYBOOK_TYPE.PROCESSOR && p.uiContexts?.find((c) => c === UiContextEnum.CoordinateLists)),
      );
    }
    setPlaybooks(filteredPlaybooks);
  }, []);

  const getPlaybooksMenuMessage = () => {
    if (isLoading) {
      return "Loading compatible playbooks...";
    } else if (analysisGroupFeedback) {
      return "The reports require a selection of mutant genomes. " + analysisGroupFeedback;
    } else if (playbooks.length === 0) {
      return "There are no Playbooks that are compatible with your current selection. ";
    } else {
      const entityName: string = FEATURE_DISPLAY_NAME[tableQuerySpec.resultType];
      return `Selected ${pluralize(entityName)} will be used for producing the report.`;
    }
  };

  return (
    <MenuListBox
      {...props}
      isLoading={isLoading}
      footNote={
        <div
          className={clsx(menuStyle.foot_note, {
            [menuStyle.foot_note_error]: analysisGroupFeedback,
          })}
          style={{ minWidth: 380 }}
        >
          {getPlaybooksMenuMessage()}
        </div>
      }
      onAction={(key) => {
        dispatch(
          updateNfPlaybookExecutionState({
            reportTableQuerySpec: tableQuerySpec,
            reportRangeSelectionLimitApplies: false,
          }),
        );
        const playbook = playbooks.find((p) => String(p.id) === key);
        dispatch(openPlaybook(playbook));
      }}
      disabledKeys={analysisGroupFeedback ? playbooks?.map((p) => String(p.id)) : undefined}
    >
      {playbooks?.map((playbook) => (
        <Item key={String(playbook.id)}>
          <MenuOptionItem
            title={playbook.nameSuffix != undefined ? `${playbook.name} : ${playbook.nameSuffix}` : playbook.name}
            description={playbook.description}
            icon={<img width={50} src={getIconElementForPlaybook(playbook).large} alt={"Playbook icon"} />}
            tag={playbook.standard ? <ProtectedResourceLogo size={16} /> : undefined}
          />
        </Item>
      ))}
    </MenuListBox>
  );
};
